/**
 * Watchers and scans: workers that never trade. A WATCHER checks market conditions on one stock (the
 * worker rule language — price, a range, today's % move, indicators, the opening gap, a time) and,
 * when one holds, tells the user, wakes the AI in its chat, pauses or ends. A SCAN runs a market
 * filter on a schedule (seconds to yearly, shared/schedule.ts) and reacts only to NEW hits.
 *
 * Data: EODHD — the real-time US trade stream (pre/after-hours too), its snapshots and daily /
 * intraday bars — so they run whether or not the IBKR gateway is up.
 */
import { z } from 'zod'
import { EtDateTimeSchema, IndicatorSchema, InstrumentSchema, OperandSchema, SymbolSchema, type Indicator, type Instrument } from './worker'
import { ScheduleSchema, type Schedule } from './schedule'

const Price = z.number().finite().positive().max(1_000_000)
const Cmp = z.enum(['>=', '<='])
const RuleId = z.string().trim().regex(/^[A-Za-z0-9_-]{1,32}$/, 'rule id: letters, digits, _ or -')

/** What makes a watcher rule true. */
export const WatchTriggerSchema = z.discriminatedUnion('on', [
  /** The stock (or another instrument) trades at or above / below a price. */
  z.object({ on: z.literal('price'), instrument: InstrumentSchema.optional(), op: Cmp, value: Price }),
  /** The price is inside (or outside) a range — "enters the 100.30–101.50 zone". */
  z.object({
    on: z.literal('range'),
    instrument: InstrumentSchema.optional(),
    low: Price,
    high: Price,
    side: z.enum(['inside', 'outside']).default('inside'),
  }),
  /** Today's move vs the prior close (or vs today's open), in % — e.g. pct -3 with "<=" = down 3% or more. */
  z.object({
    on: z.literal('move'),
    instrument: InstrumentSchema.optional(),
    op: Cmp,
    pct: z.number().finite().min(-100).max(1000),
    from: z.enum(['prevClose', 'open']).default('prevClose'),
  }),
  /** An indicator condition (the worker language): live, or judged on completed bars ("a daily close above SMA 200"). */
  z.object({
    on: z.literal('indicator'),
    left: IndicatorSchema,
    op: z.enum(['>=', '<=', 'crossAbove', 'crossBelow'], {
      required_error: 'indicator trigger needs op: ">=", "<=", "crossAbove" or "crossBelow" — e.g. {"on":"indicator","left":{"kind":"rsi","period":14},"op":"<=","right":30}',
    }),
    right: OperandSchema,
    at: z.enum(['live', 'close']).default('live'),
  }),
  /** The opening gap of a session: opens at or below / above a price, or pct% vs the prior close. */
  z.object({ on: z.literal('open'), op: Cmp, value: Price.optional(), pct: z.number().finite().min(-90).max(500).optional() }),
  /** A moment (US Eastern). */
  z.object({ on: z.literal('time'), at: EtDateTimeSchema }),
  /** Every session at or after this US/Eastern time (until the close) — with guards: "at 15:45, if it is still below X". */
  z.object({
    on: z.literal('daily'),
    at: z
      .string()
      .trim()
      .refine((x) => /^([01]\d|2[0-3]):[0-5]\d$/.test(x), 'expected "HH:MM" (US/Eastern)'),
  }),
])
export type WatchTrigger = z.infer<typeof WatchTriggerSchema>

/** Extra conditions that must hold when the trigger does. */
export const WatchGuardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('price'), instrument: InstrumentSchema.optional(), op: Cmp, value: Price }),
  z.object({
    kind: z.literal('indicator'),
    left: IndicatorSchema,
    op: z.enum(['>=', '<=']),
    right: OperandSchema,
    at: z.enum(['live', 'close']).default('live'),
    not: z.boolean().default(false),
  }),
])
export type WatchGuard = z.infer<typeof WatchGuardSchema>

export const WatchActionSchema = z.discriminatedUnion('do', [
  /** Tell the user (Telegram + the chat) with the live numbers. */
  z.object({ do: z.literal('notify'), message: z.string().trim().min(1).max(300) }),
  /** Wake the AI in the watcher's chat with this question — it looks at the news, the plan, the chart and answers (Telegram + the chat). */
  z.object({ do: z.literal('askAI'), message: z.string().trim().min(1).max(500) }),
  /** Stop checking until the user resumes it. */
  z.object({ do: z.literal('pause'), message: z.string().trim().max(300).optional() }),
  /** This watcher is done. */
  z.object({ do: z.literal('end'), message: z.string().trim().max(300).optional() }),
])
export type WatchAction = z.infer<typeof WatchActionSchema>

/**
 * Without `repeat` a rule fires once. With it: "rearm" fires each time the condition BECOMES true
 * (after being false in between), "while" fires again every `cooldownMin` for as long as it stays
 * true; never more often than `cooldownMin`, at most `max` times.
 */
export const RepeatSchema = z.object({
  mode: z.enum(['rearm', 'while']).default('rearm'),
  cooldownMin: z.number().int().min(1).max(525_600).default(30),
  max: z.number().int().min(1).max(1000).optional(),
})
export type Repeat = z.infer<typeof RepeatSchema>

export const WatchRuleSchema = z.object({
  id: RuleId,
  when: WatchTriggerSchema,
  if: z.array(WatchGuardSchema).max(6).default([]),
  then: z.array(WatchActionSchema).min(1).max(4),
  repeat: RepeatSchema.optional(),
})
export type WatchRule = z.infer<typeof WatchRuleSchema>

export const WatchSpecSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    symbol: SymbolSchema,
    /** An index or another security type for the main instrument (default: a US stock). */
    secType: z.enum(['STK', 'IND']).default('STK'),
    /** Why this watcher exists — the plan and levels in words; the AI reads it when woken. */
    note: z.string().trim().max(2000).optional(),
    /** When price / indicator rules are checked: the regular session, pre + regular + after-hours, or around the clock. */
    hours: z.enum(['session', 'extended', 'always']).default('session'),
    rules: z.array(WatchRuleSchema).min(1).max(12),
    /** The watcher ends by itself at this moment (US Eastern). */
    until: EtDateTimeSchema.optional(),
  })
  .superRefine((s, ctx) => {
    const ids = new Set<string>()
    s.rules.forEach((r, i) => {
      if (ids.has(r.id)) ctx.addIssue({ code: 'custom', path: ['rules', i, 'id'], message: `duplicate rule id "${r.id}"` })
      ids.add(r.id)
      if (r.when.on === 'range' && r.when.low >= r.when.high) ctx.addIssue({ code: 'custom', path: ['rules', i, 'when'], message: 'range: low must be below high' })
      if (r.when.on === 'open' && (r.when.value == null) === (r.when.pct == null))
        ctx.addIssue({ code: 'custom', path: ['rules', i, 'when'], message: 'open trigger: give value OR pct' })
    })
  })
export type WatchSpec = z.infer<typeof WatchSpecSchema>
export type WatchSpecInput = z.input<typeof WatchSpecSchema>

/* ── scans ──────────────────────────────────────────────────────────────── */

/** EODHD screener fields a filter can use. */
export const SCREENER_FIELDS = ['market_capitalization', 'earnings_share', 'dividend_yield', 'refund_1d_p', 'refund_5d_p', 'adjusted_close', 'avgvol_1d', 'avgvol_200d', 'exchange', 'sector', 'industry', 'code', 'name'] as const
const FilterTriple = z.tuple([z.enum(SCREENER_FIELDS), z.enum(['=', '!=', '>', '<', '>=', '<=', 'match']), z.union([z.number(), z.string()])])

/** Which stocks a scan looks at. */
export const UniverseSchema = z.discriminatedUnion('kind', [
  /** EODHD's screener (fundamentals + 1/5-day returns, refreshed about daily): up to 100 names. */
  z.object({
    kind: z.literal('screener'),
    filters: z.array(FilterTriple).min(1).max(12),
    signals: z.string().trim().max(200).optional(),
    sort: z.string().trim().regex(/^[a-z_0-9]+\.(asc|desc)$/, 'sort: "field.asc" or "field.desc"').optional(),
    limit: z.number().int().min(1).max(100).default(50),
  }),
  /** A fixed list, an index's members, or the user's own watchlist / portfolio — checked on live prices. */
  z.object({
    kind: z.literal('list'),
    preset: z.enum(['sp500', 'nasdaq100', 'dow30', 'watchlist', 'portfolio']).optional(),
    symbols: z.array(SymbolSchema).max(300).default([]),
  }),
])
export type Universe = z.infer<typeof UniverseSchema>

/** A condition every hit must meet (on today's live numbers and daily bars). */
export const ScanConditionSchema = z.discriminatedUnion('on', [
  /** Today's % change vs the prior close. */
  z.object({ on: z.literal('change'), op: Cmp, pct: z.number().finite().min(-100).max(1000) }),
  /** The opening gap vs the prior close, %. */
  z.object({ on: z.literal('gap'), op: Cmp, pct: z.number().finite().min(-100).max(1000) }),
  z.object({ on: z.literal('price'), op: Cmp, value: Price }),
  /** Distance below the 52-week high, % (e.g. "<=" -20 = at least 20% below it). */
  z.object({ on: z.literal('fromHigh'), op: Cmp, pct: z.number().finite().min(-100).max(0) }),
  /** Today's volume vs its average (time-adjusted in the session). */
  z.object({ on: z.literal('rvol'), op: Cmp, value: z.number().finite().positive().max(1000) }),
  /** Daily RSI 14. */
  z.object({ on: z.literal('rsi'), op: Cmp, value: z.number().finite().min(0).max(100) }),
  /** Price vs its daily SMA of `period` days. */
  z.object({ on: z.literal('sma'), period: z.number().int().min(5).max(250), side: z.enum(['above', 'below']) }),
  /** Market value, USD. */
  z.object({ on: z.literal('marketCap'), op: Cmp, value: z.number().finite().positive() }),
])
export type ScanCondition = z.infer<typeof ScanConditionSchema>

export const ScanSpecSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** What the scan is for — the AI reads it when it analyzes the hits. */
  note: z.string().trim().max(2000).optional(),
  universe: UniverseSchema,
  where: z.array(ScanConditionSchema).max(10).default([]),
  schedule: ScheduleSchema,
  /** Only symbols not seen in the last `cooldownDays` trigger (default: new hits only). */
  newOnly: z.boolean().default(true),
  cooldownDays: z.number().int().min(0).max(90).default(5),
  /** At most this many hits per run go to the notification / the AI (the best-ranked). */
  maxHits: z.number().int().min(1).max(25).default(8),
  /** notify = the list to Telegram + the chat; askAI = the AI analyzes the new hits in the scan's chat; both. */
  action: z.enum(['notify', 'askAI', 'both']).default('both'),
  /** Rank hits by: the biggest drop today (default), gap, distance from the high, relative volume. */
  rank: z.enum(['change', 'gap', 'fromHigh', 'rvol', 'marketCap']).default('change'),
})
export type ScanSpec = z.infer<typeof ScanSpecSchema>
export type ScanSpecInput = z.input<typeof ScanSpecSchema>

/* ── records ────────────────────────────────────────────────────────────── */

export type WatcherKind = 'watch' | 'scan'
export type WatcherStatus = 'active' | 'paused' | 'done'

/** Per rule: how often it fired, when, and whether it is armed again (rearm). */
export interface WatchRuleState {
  fires: number
  lastAt: number | null
  armed: boolean
  done: boolean
  /** The last evaluation: does the condition hold right now? */
  holds: boolean | null
}

export interface WatchRuntime {
  rules: Record<string, WatchRuleState>
  /** Live crosses: the side each indicator trigger was on at the last check. */
  crossSide: Record<string, boolean>
  /** Scans: symbol → when it was last a hit (epoch ms). */
  seen?: Record<string, number>
  /** Scans: the last run's hits. */
  lastHits?: ScanHit[]
  lastRunAt?: number | null
  /** AI wakeups today (a watcher is limited to 12 a day). */
  aiDay?: string
  aiCount?: number
}

export interface ScanHit {
  symbol: string
  name: string | null
  price: number | null
  changePct: number | null
  gapPct: number | null
  fromHighPct: number | null
  rvol: number | null
  rsi: number | null
  marketCap: number | null
  sector: string | null
  isNew: boolean
}

/** A watcher / scan as the app shows it. */
export interface WatcherRecord {
  id: string
  kind: WatcherKind
  name: string
  status: WatcherStatus
  chatId: string | null
  chatTitle: string | null
  createdBy: 'user' | 'ai'
  spec: WatchSpec | ScanSpec
  runtime: WatchRuntime
  /** Scans: the next run. */
  nextRunAt: number | null
  endsAt: number | null
  createdAt: number
  updatedAt: number
}

/** One rule, in words, with its live state. */
export interface WatchRuleView {
  id: string
  text: string
  /** "PAYX 101.23 · range 100.30–101.50" — what the condition looks at now (null until data arrives). */
  now: string | null
  holds: boolean | null
  fires: number
  lastAt: number | null
  done: boolean
}

export interface WatcherEventRecord {
  id: string
  kind: string
  message: string
  data: unknown
  createdAt: number
}

export interface WatcherDetail extends WatcherRecord {
  /** A watcher: its price now and where it comes from. */
  quote: { price: number | null; changePct: number | null; source: string; at: number | null } | null
  rules: WatchRuleView[]
  /** When its rules are checked, in words (and whether that is now). */
  hoursText: string
  checkingNow: boolean
  scheduleText: string | null
  events: WatcherEventRecord[]
}

export const isScan = (spec: WatchSpec | ScanSpec): spec is ScanSpec => 'universe' in spec
export type { Indicator, Instrument, Schedule }
